import { useMemo } from 'react'
import { featureManifests } from '@/app/navigation/manifests'
import { useFeatureSelector } from '@/platform/state/hooks'
import { hasAllPermissions } from '@/platform/authorization/permissions'
import { AppShell, type ShellNavigationEntry, type ShellNavigationItem, type ShellNavigationSubgroup } from '@/platform/shell/AppShell'

function createNavigation(): ShellNavigationEntry[] {
  const entries: ShellNavigationEntry[] = []
  const groups = new Map<string, Extract<ShellNavigationEntry, { kind: 'group' }>>()

  for (const feature of featureManifests) {
    if (feature.hideFromNavigation) continue
    const item: ShellNavigationItem = { id: feature.id, path: feature.navPath, label: feature.label }
    if (!feature.navigationGroup) {
      entries.push(item)
      continue
    }
    let group = groups.get(feature.navigationGroup.id)
    if (!group) {
      group = { kind: 'group', id: feature.navigationGroup.id, label: feature.navigationGroup.label, items: [] }
      groups.set(group.id, group)
      entries.push(group)
    }
    if (!feature.navigationSubgroup) {
      group.items.push(item)
      continue
    }
    let subgroup = group.items.find((candidate): candidate is ShellNavigationSubgroup => 'kind' in candidate && candidate.id === feature.navigationSubgroup!.id)
    if (!subgroup) {
      subgroup = { kind: 'subgroup', id: feature.navigationSubgroup.id, label: feature.navigationSubgroup.label, items: [] }
      group.items.push(subgroup)
    }
    subgroup.items.push(item)
  }
  return entries
}

const navigation = createNavigation()

export function AppShellLayout() {
  const authorization = useFeatureSelector((state) => state.session.authorization)
  const isAdmin = useFeatureSelector((state) => state.session.user?.role === 'admin' || state.session.user?.role === 'super_admin')
  const isSuperAdmin = useFeatureSelector((state) => state.session.user?.role === 'super_admin')
  const visibleNavigation = useMemo(() => {
    const canAccess = (item: ShellNavigationItem) => {
      const feature = featureManifests.find((candidate) => candidate.id === item.id)
      return !!feature && hasAllPermissions(authorization, feature.requiredPermissions) && (!feature.adminOnly || isAdmin) && (!feature.superAdminOnly || isSuperAdmin)
    }
    const visible: ShellNavigationEntry[] = []
    for (const entry of navigation) {
      if ('kind' in entry) {
        const items: Array<ShellNavigationItem | ShellNavigationSubgroup> = []
        for (const item of entry.items) {
          if ('kind' in item) {
            const children = item.items.filter(canAccess)
            if (children.length) items.push({ ...item, items: children })
            continue
          }
          if (canAccess(item)) items.push(item)
        }
        if (items.length) visible.push({ ...entry, items })
        continue
      }
      if (canAccess(entry)) visible.push(entry)
    }
    return visible
  }, [authorization, isAdmin, isSuperAdmin])

  return <AppShell homePath="/dashboard" navigation={visibleNavigation} />
}
